import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  createSafetyLog,
  deleteSafetyLog,
  getSafetyLogById,
  getSafetyLogPdf,
  getSafetyLogs,
  getSafetyLogsByDate,
  signSafetyLog,
  updateSafetyLog,
} from './safety-log.api';
import type { CreateSafetyLogFormValues, UpdateSafetyLogFormValues } from './safety-log.dto';

const ROOT_KEY = 'safety-logs';

export const useSafetyLogsQuery = (limit: number) =>
  useInfiniteQuery({
    queryKey: [ROOT_KEY, limit],
    queryFn: ({ pageParam }) => getSafetyLogs(limit, pageParam),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
  });

export const useSafetyLogByIdQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-id', id],
    queryFn: () => getSafetyLogById(id!),
    enabled: !!id,
  });

export const useSafetyLogsByDateQuery = (date: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-date', date],
    queryFn: () => getSafetyLogsByDate(date!),
    enabled: !!date,
  });

export const useCreateSafetyLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateSafetyLogFormValues) => createSafetyLog(values),
    onSuccess(_, { date }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-date', date] });
    },
  });
};

export const useUpdateSafetyLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateSafetyLogFormValues) => updateSafetyLog(values),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-id', id] });
    },
  });
};

export const useDeleteSafetyLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteSafetyLog(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
};

export const useSignSafetyLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => signSafetyLog(id),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-id', id] });
    },
  });
};

export const useSafetyLogPdfMutation = () =>
  useMutation({
    mutationFn: ({ id, timezone }: { id: string; timezone?: string }) =>
      getSafetyLogPdf(id, timezone),
  });
